/* eslint-disable no-underscore-dangle */
import React, { useCallback, useRef, useEffect } from 'react';
import { useDraggable } from '@dnd-kit/core';
import styles from 'Components/PdfFormTemplates/PdfFormEditor/pdfFormEditor.module.css';
import { EditorField, getTokenDisplayLabel } from '../types';

interface Props {
  field: EditorField;
  isSelected: boolean;
  scale: number;
  onSelect: (clientId: string) => void;
  onToggleSelect: (clientId: string) => void;
  onResize: (clientId: string, width: number, height: number, x?: number, y?: number) => void;
  onResizeBegin?: () => void;
  onResizeEnd?: () => void;
  snapDimensions: (field: EditorField, width: number, height: number) => { width: number; height: number };
  groupTransform?: { x: number; y: number } | null;
}

export function DraggableField({ field, isSelected, scale, onSelect, onToggleSelect, onResize, onResizeBegin, onResizeEnd, snapDimensions, groupTransform }: Props) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: field._clientId,
    data: { type: 'existing-field', field },
  });

  const resizing = useRef(false);
  const startPos = useRef({ x: 0, y: 0, w: 0, h: 0 });
  const cleanupRef = useRef<(() => void) | null>(null);

  // Cleanup resize listeners on unmount
  useEffect(
    () => () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    },
    []
  );

  let typeClass = '';
  if (field.field_type === 'checkbox') {
    typeClass = styles.fieldOverlayCheckbox;
  } else if (field.field_type === 'date') {
    typeClass = styles.fieldOverlayDate;
  } else if (field.field_type === 'signature') {
    typeClass = styles.fieldOverlaySignature;
  } else if (field.field_type === 'initial') {
    typeClass = styles.fieldOverlayInitial;
  } else if (field.field_type === 'email') {
    typeClass = styles.fieldOverlayEmail;
  } else if (field.field_type === 'phone') {
    typeClass = styles.fieldOverlayPhone;
  } else if (field.field_type === 'number') {
    typeClass = styles.fieldOverlayNumber;
  }

  const scaledHeight = field.height * scale;
  const labelFontSize = Math.max(9, Math.min(16, scaledHeight * 0.6));
  let appliedTransform: string | undefined;
  if (transform) {
    appliedTransform = `translate(${transform.x}px, ${transform.y}px)`;
  } else if (groupTransform) {
    appliedTransform = `translate(${groupTransform.x}px, ${groupTransform.y}px)`;
  }

  const style: React.CSSProperties = {
    position: 'absolute',
    left: field.x * scale,
    top: field.y * scale,
    width: field.width * scale,
    height: scaledHeight,
    transform: appliedTransform,
    zIndex: isSelected ? 10 : 1,
    fontSize: labelFontSize,
  };

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (e.shiftKey || e.metaKey || e.ctrlKey) {
        onToggleSelect(field._clientId);
      } else {
        onSelect(field._clientId);
      }
    },
    [field._clientId, onSelect, onToggleSelect]
  );

  const startField = useRef({ x: 0, y: 0 });

  const onResizeStart = useCallback(
    (e: React.PointerEvent, direction: string) => {
      e.stopPropagation();
      e.preventDefault();
      if (onResizeBegin) onResizeBegin();
      resizing.current = true;
      startPos.current = {
        x: e.clientX,
        y: e.clientY,
        w: field.width * scale,
        h: field.height * scale,
      };
      startField.current = { x: field.x, y: field.y };

      const finishResize = () => {
        if (!resizing.current) return;
        resizing.current = false;
        if (onResizeEnd) onResizeEnd();
      };

      const onPointerMove = (ev: PointerEvent) => {
        if (!resizing.current) return;
        const dx = ev.clientX - startPos.current.x;
        const dy = ev.clientY - startPos.current.y;

        let newW = startPos.current.w;
        let newH = startPos.current.h;
        let newX = startField.current.x;
        let newY = startField.current.y;

        // Right edge grows right
        if (direction === 'se' || direction === 'e' || direction === 'ne') newW = Math.max(20, startPos.current.w + dx);
        // Left edge grows left (move x, shrink width)
        if (direction === 'sw' || direction === 'w' || direction === 'nw') {
          newW = Math.max(20, startPos.current.w - dx);
          newX = startField.current.x + (startPos.current.w - newW) / scale;
        }
        // Bottom edge grows down
        if (direction === 'se' || direction === 's' || direction === 'sw') newH = Math.max(14, startPos.current.h + dy);
        // Top edge grows up (move y, shrink height)
        if (direction === 'ne' || direction === 'n' || direction === 'nw') {
          newH = Math.max(14, startPos.current.h - dy);
          newY = startField.current.y + (startPos.current.h - newH) / scale;
        }

        // Snap dimensions to match other fields
        const unscaledW = newW / scale;
        const unscaledH = newH / scale;
        const snapped = snapDimensions(field, unscaledW, unscaledH);

        // Adjust position if resizing from left/top edge and dimension snapped
        if (direction === 'sw' || direction === 'w' || direction === 'nw') {
          newX = startField.current.x + (startPos.current.w / scale - snapped.width);
        }
        if (direction === 'ne' || direction === 'n' || direction === 'nw') {
          newY = startField.current.y + (startPos.current.h / scale - snapped.height);
        }

        onResize(field._clientId, snapped.width, snapped.height, newX, newY);
      };

      const onPointerUp = () => {
        finishResize();
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        cleanupRef.current = null;
      };

      // Store cleanup so unmount can remove listeners
      cleanupRef.current = () => {
        finishResize();
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
      };

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    },
    [field._clientId, field.x, field.y, field.width, field.height, scale, onResize, onResizeBegin, onResizeEnd, snapDimensions]
  );

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={setNodeRef}
      className={`${styles.fieldOverlay} ${typeClass} ${isSelected ? styles.fieldOverlaySelected : ''}`}
      style={style}
      onClick={handleClick}
      data-field-overlay
      {...attributes} // eslint-disable-line react/jsx-props-no-spreading
    >
      {/* Drag listeners only on the label area so resize handles don't trigger dragging */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <span
        className={styles.fieldLabel}
        {...listeners} // eslint-disable-line react/jsx-props-no-spreading
      >
        {getTokenDisplayLabel(field.field_name) || field.field_type}
      </span>
      {isSelected && (
        <>
          {/* eslint-disable jsx-a11y/no-static-element-interactions */}
          <div className={`${styles.resizeHandle} ${styles.resizeHandleNW}`} onPointerDown={(e) => onResizeStart(e, 'nw')} />
          <div className={`${styles.resizeHandle} ${styles.resizeHandleN}`} onPointerDown={(e) => onResizeStart(e, 'n')} />
          <div className={`${styles.resizeHandle} ${styles.resizeHandleNE}`} onPointerDown={(e) => onResizeStart(e, 'ne')} />
          <div className={`${styles.resizeHandle} ${styles.resizeHandleE}`} onPointerDown={(e) => onResizeStart(e, 'e')} />
          <div className={`${styles.resizeHandle} ${styles.resizeHandleSE}`} onPointerDown={(e) => onResizeStart(e, 'se')} />
          <div className={`${styles.resizeHandle} ${styles.resizeHandleS}`} onPointerDown={(e) => onResizeStart(e, 's')} />
          <div className={`${styles.resizeHandle} ${styles.resizeHandleSW}`} onPointerDown={(e) => onResizeStart(e, 'sw')} />
          <div className={`${styles.resizeHandle} ${styles.resizeHandleW}`} onPointerDown={(e) => onResizeStart(e, 'w')} />
          {/* eslint-enable jsx-a11y/no-static-element-interactions */}
        </>
      )}
    </div>
  );
}
